<script>
import Layout from '@/layouts/main.vue';
import { api } from '@/helpers/common/api';
import * as echarts from 'echarts';
import EngajamentoUsuariosModal from '@/components/crm/dashboard/EngajamentoUsuariosModal.vue';

const MESES = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];

// Paleta de dados (tema Velzon) — legível em light e dark.
const PALETTE = ['#405189', '#0ab39c', '#f7b84b', '#f06548', '#299cdb', '#3577f1', '#a066ff', '#f672a7', '#51d28c', '#878a99'];

const fmt = (n) => Number(n || 0).toLocaleString('pt-BR');

// Formato compacto pros rótulos de eixo (evita sobreposição): 3k, 15k, 1,2M.
// Os valores cheios continuam nas barras e tooltips via fmt().
const fmtEixo = (v) => {
  const n = Number(v) || 0;
  const abs = Math.abs(n);
  if (abs >= 1000000) return `${(n / 1000000).toLocaleString('pt-BR', { maximumFractionDigits: 1 })}M`;
  if (abs >= 1000) return `${(n / 1000).toLocaleString('pt-BR', { maximumFractionDigits: 1 })}k`;
  return fmt(n);
};

export default {
  name: 'AppDashboard',
  components: { Layout, EngajamentoUsuariosModal },
  data() {
    return {
      loading: true,
      erro: null,
      periodo: 30,
      periodos: [7, 30, 90],
      dados: null,
      engajamento: null,
      modalEng: false,
      charts: [],
      animado: { contatos: 0, conversas: 0, mensagens: 0, campanhas: 0, canais: 0, alcance: 0 },
    };
  },
  computed: {
    d() { return this.dados || {}; },
    contatos() { return this.d.contatos || {}; },
    conversas() { return this.d.conversas || {}; },
    mensagens() { return this.d.mensagens || {}; },
    campanhas() { return this.d.campanhas || {}; },
    canais() { return this.d.canais || {}; },
    alcance() { return this.d.alcance || {}; },
    canaisConectados() { return (this.canais.oficiaisConectados || 0) + (this.canais.baileysConectados || 0); },
    canaisTotal() { return (this.canais.oficiaisTotal || 0) + (this.canais.baileysTotal || 0); },
    // Soma de participações: a mesma pessoa conta uma vez por grupo/canal.
    alcanceTotal() { return (this.alcance.gruposParticipantes || 0) + (this.alcance.newslettersInscritos || 0); },
    gruposAnalise() { return this.d.analiseGrupos || { disponivel: false }; },
    temAnaliseGrupos() { return Boolean(this.gruposAnalise.disponivel); },
    // Pessoas distintas nos grupos (null enquanto o sync não gravou a lista).
    alcanceUnicoGrupos() {
      return this.temAnaliseGrupos ? (this.gruposAnalise.pessoasUnicas || 0) : null;
    },
    // Quanto a soma antiga inflava por contar a mesma pessoa em vários grupos.
    repeticoesGrupos() { return this.temAnaliseGrupos ? (this.gruposAnalise.repeticoes || 0) : 0; },
    gruposChips() {
      const g = this.gruposAnalise;
      const base = g.base || {};
      const pct = g.participacoes ? Math.round((g.repeticoes / g.participacoes) * 100) : 0;
      return [
        { label: 'pessoas distintas', value: g.pessoasUnicas || 0, icon: 'ri-user-3-line', hex: '#0ab39c' },
        { label: 'participações somadas', value: g.participacoes || 0, icon: 'ri-stack-line', hex: '#f672a7' },
        { label: `repetições (${pct}% da soma)`, value: g.repeticoes || 0, icon: 'ri-file-copy-2-line', hex: '#f7b84b' },
        { label: 'já são contato da base', value: base.naBase || 0, icon: 'ri-contacts-book-2-line', hex: '#405189' },
        { label: 'sem telefone visível', value: g.semTelefone || 0, icon: 'ri-eye-off-line', hex: '#878a99' },
      ];
    },
    atualizadoEm() {
      if (!this.d.geradoEm) return '';
      try { return new Date(this.d.geradoEm).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' }); } catch (_e) { return ''; }
    },
    kpis() {
      const c = this.contatos; const conv = this.conversas; const m = this.mensagens;
      const camp = this.campanhas; const ca = this.canais; const al = this.alcance;
      return [
        { key: 'contatos', label: 'Contatos na base', icon: 'ri-contacts-book-2-line', hex: '#405189', delta: c.deltaPct, sub: `+${fmt(c.novosHoje)} hoje · +${fmt(c.novos7d)} em 7d` },
        { key: 'conversas', label: 'Conversas abertas', icon: 'ri-chat-3-line', hex: '#299cdb', delta: null, sub: `${fmt(conv.naoLidas)} não lidas · ${fmt(conv.fechadas)} encerradas` },
        { key: 'mensagens', label: 'Mensagens hoje', icon: 'ri-message-2-line', hex: '#0ab39c', delta: m.deltaPct, sub: `${fmt(m.entradaHoje)} recebidas · ${fmt(m.saidaHoje)} enviadas` },
        { key: 'campanhas', label: 'Campanhas ativas', icon: 'ri-megaphone-line', hex: '#f7b84b', delta: null, sub: `${fmt(camp.enviadosHoje)} envios hoje · ${camp.taxaEntrega || 0}% entrega` },
        { key: 'canais', label: 'Canais conectados', icon: 'ri-base-station-line', hex: '#6559cc', suffix: `/${fmt(this.canaisTotal)}`, delta: null, sub: `${fmt(ca.oficiaisConectados)} oficiais · ${fmt(ca.baileysConectados)} baileys` },
        {
          key: 'alcance',
          // Sem a lista de participantes só existe a soma de participações —
          // o rótulo muda para não prometer "pessoas" quando não são.
          label: this.alcanceUnicoGrupos === null ? 'Participações em grupos + notícias' : 'Pessoas distintas em grupos',
          icon: 'ri-group-2-line',
          hex: '#f672a7',
          delta: null,
          sub: this.alcanceUnicoGrupos === null
            ? `${fmt(al.grupos)} grupos · ${fmt(al.newsletters)} canais`
            : `em ${fmt(al.grupos)} grupos · ${fmt(this.repeticoesGrupos)} repetições descontadas`,
        },
      ];
    },
    // Flags de dados para empty-states.
    temFunil() { return (this.conversas.funil || []).some((f) => f.total > 0); },
    temStatus() { const s = this.contatos.porStatus || {}; return (s.ativo || 0) + (s.inativo || 0) + (s.bloqueado || 0) > 0; },
    temSerie() { return (this.mensagens.serie || []).some((p) => p.entrada > 0 || p.saida > 0); },
    temFunilCamp() { const f = this.campanhas.funil || {}; return (f.destinatarios || 0) + (f.enviados || 0) > 0; },
    temCrescimento() { return (this.contatos.crescimento || []).some((m) => m.total > 0); },
    temCarga() { return (this.conversas.carga || []).length > 0; },
    temCanais() { return this.canaisTotal > 0; },
    temEtiquetas() { return (this.d.topEtiquetas || []).length > 0; },
    // Engajamento por usuário.
    eng() { return this.engajamento || {}; },
    engResumo() { return this.eng.resumo || {}; },
    engUsuarios() { return this.eng.porUsuario || []; },
    temEngajamento() { return this.engUsuarios.some((u) => u.total > 0); },
    engChips() {
      const r = this.engResumo;
      return [
        { label: 'usuários ativos', value: r.usuariosAtivos, icon: 'ri-user-follow-line', hex: '#405189' },
        { label: 'msgs conversa', value: r.mensagensConversa, icon: 'ri-chat-3-line', hex: '#0ab39c' },
        { label: 'msgs grupo', value: r.mensagensGrupo, icon: 'ri-group-line', hex: '#299cdb' },
        { label: 'msgs canal', value: r.mensagensCanal, icon: 'ri-broadcast-line', hex: '#f7b84b' },
        { label: 'campanhas', value: r.campanhas, icon: 'ri-megaphone-line', hex: '#6559cc' },
        { label: 'contatos criados', value: r.contatosCriados, icon: 'ri-user-add-line', hex: '#f672a7' },
      ];
    },
    // Enquetes e testes (grupos + canais).
    enq() { return this.d.enquetes || { disponivel: false }; },
    temEnquetes() { return Boolean(this.enq.disponivel); },
    enqChips() {
      const e = this.enq;
      return [
        { label: 'enquetes publicadas', value: e.total || 0, icon: 'ri-bar-chart-box-line', hex: '#405189' },
        { label: 'testes (quiz)', value: e.testes || 0, icon: 'ri-question-answer-line', hex: '#6559cc' },
        { label: 'votos apurados', value: e.votosTotais || 0, icon: 'ri-check-double-line', hex: '#0ab39c' },
        { label: 'média de votantes', value: e.mediaVotantes || 0, icon: 'ri-group-line', hex: '#299cdb' },
        { label: `com resposta (${e.taxaResposta || 0}%)`, value: e.comResposta || 0, icon: 'ri-thumb-up-line', hex: '#f7b84b' },
        { label: `novas em ${this.periodo}d`, value: e.novasPeriodo || 0, icon: 'ri-add-circle-line', hex: '#f672a7' },
      ];
    },
    enqTemTop() { return (this.enq.top || []).length > 0; },
    enqTemSerie() { return (this.enq.serie || []).some((p) => p.total > 0); },
  },
  async mounted() {
    this._raf = {};
    document.body.classList.add('dashboard-page');
    await this.carregar();
    window.addEventListener('resize', this.resizeCharts);
    this._themeObserver = new MutationObserver((muts) => {
      if (muts.some((mm) => mm.attributeName === 'data-bs-theme')) this.rerenderTema();
    });
    this._themeObserver.observe(document.documentElement, { attributes: true, attributeFilter: ['data-bs-theme'] });
  },
  beforeUnmount() {
    document.body.classList.remove('dashboard-page');
    window.removeEventListener('resize', this.resizeCharts);
    if (this._themeObserver) this._themeObserver.disconnect();
    Object.values(this._raf || {}).forEach((id) => cancelAnimationFrame(id));
    this.disposeCharts();
  },
  methods: {
    fmt,
    /** @param {boolean} forcar ignora o cache de 2 min do servidor (botão Atualizar). */
    async carregar(forcar = false) {
      this.loading = true;
      this.erro = null;
      try {
        const extra = forcar === true ? '&atualizar=1' : '';
        const [res, eng] = await Promise.all([
          api.read(`/dashboard/resumo?periodo=${this.periodo}${extra}`),
          api.read(`/dashboard/engajamento?periodo=${this.periodo}${extra}`),
        ]);
        if (!res || res.error) { this.erro = (res && res.error) || 'Não foi possível carregar os indicadores.'; return; }
        this.dados = res;
        this.engajamento = eng && !eng.error ? eng : null;
      } catch (e) {
        this.erro = e.message || 'Falha ao carregar o dashboard.';
      } finally {
        this.loading = false;
        this.$nextTick(() => { this.renderCharts(); this.iniciarContadores(); });
      }
    },
    async trocarPeriodo(p) {
      if (this.periodo === p || this.loading) return;
      this.periodo = p;
      // Não descarta aqui: os charts antigos ficam visíveis durante o fetch e
      // renderCharts() cuida do dispose+recriação quando os novos dados chegam.
      await this.carregar();
    },

    // ---------- Contadores animados ----------
    tween(key, to, dur = 900) {
      const from = this.animado[key] || 0;
      const inicio = performance.now();
      cancelAnimationFrame(this._raf[key]);
      const passo = (agora) => {
        const p = Math.min(1, (agora - inicio) / dur);
        const eased = 1 - (1 - p) ** 3;
        this.animado[key] = Math.round(from + (to - from) * eased);
        if (p < 1) this._raf[key] = requestAnimationFrame(passo);
      };
      this._raf[key] = requestAnimationFrame(passo);
    },
    iniciarContadores() {
      if (!this.dados) return;
      this.tween('contatos', this.contatos.total || 0);
      this.tween('conversas', this.conversas.abertas || 0);
      this.tween('mensagens', this.mensagens.hoje || 0);
      this.tween('campanhas', this.campanhas.ativas || 0);
      this.tween('canais', this.canaisConectados);
      // Pessoas distintas quando temos a lista; senão, a soma de participações.
      this.tween('alcance', this.alcanceUnicoGrupos === null ? this.alcanceTotal : this.alcanceUnicoGrupos);
    },

    // ---------- Echarts (tema-aware) ----------
    tema() {
      const dark = document.documentElement.getAttribute('data-bs-theme') === 'dark';
      // `card` = fundo do .dash-card, usado como borda das fatias dos donuts
      // para elas parecerem separadas em vez de coladas.
      return dark
        ? { text: '#ced4da', muted: '#9aa4ad', axis: 'rgba(255,255,255,0.14)', split: 'rgba(255,255,255,0.06)', tipBg: '#2b3035', tipBorder: 'rgba(255,255,255,0.12)', tipText: '#e9ecef', card: '#2a2f34' }
        : { text: '#495057', muted: '#878a99', axis: '#e9ebec', split: '#f4f6f8', tipBg: '#ffffff', tipBorder: '#e9ebec', tipText: '#495057', card: '#ffffff' };
    },
    tooltipBase(t) {
      // ECharts fixa z-index:9999999 no tooltip (anexado ao <body>), o que o
      // faz passar por cima da topbar (z-index 1002) e da bottom-nav (1035).
      // Rebaixamos abaixo da topbar para que a barra fixa fique sempre na frente
      // (modais 1055+ continuam acima de tudo).
      return { backgroundColor: t.tipBg, borderColor: t.tipBorder, borderWidth: 1, textStyle: { color: t.tipText, fontSize: 12 }, extraCssText: 'z-index:1001;box-shadow:0 4px 16px rgba(0,0,0,.12);border-radius:8px;' };
    },
    initChart(ref) {
      const el = this.$refs[ref];
      if (!el) return null;
      const chart = echarts.init(el);
      this.charts.push(chart);
      return chart;
    },
    disposeCharts() {
      this.charts.forEach((c) => { try { c.dispose(); } catch (_e) { /* noop */ } });
      this.charts = [];
    },
    resizeCharts() { this.charts.forEach((c) => c.resize()); },
    rerenderTema() {
      // Ignora troca de tema no meio de um fetch de período (dados ainda são
      // os antigos); ao terminar, carregar() re-renderiza já com o tema atual.
      if (!this.dados || this.loading) return;
      this.$nextTick(() => this.renderCharts());
    },
    renderCharts() {
      if (!this.dados) return;
      // Idempotente: descarta instâncias anteriores antes de recriar, para que
      // refresh/troca-de-tema/troca-de-período nunca acumulem charts órfãos
      // nem reinicializem sobre um canvas já montado.
      this.disposeCharts();
      this.renderFunil();
      this.renderStatus();
      this.renderVolume();
      this.renderFunilCamp();
      this.renderCrescimento();
      this.renderCarga();
      this.renderCanais();
      this.renderEtiquetas();
      this.renderGrupos();
      this.renderEnquetes();
      this.renderEngajamento();
    },

    /**
     * Enquetes e testes: origem (donut grupos×canais), série de publicações no
     * período e ranking das mais votadas. Sem enquetes a seção nem aparece.
     */
    renderEnquetes() {
      if (!this.temEnquetes) return;
      const e = this.enq;
      const t = this.tema();

      // 1) Origem: grupos × canais.
      const chartOrig = this.initChart('cEnqOrig');
      if (chartOrig) {
        const po = e.porOrigem || {};
        const data = [
          { name: 'Grupos', value: po.grupos || 0, itemStyle: { color: '#0ab39c' } },
          { name: 'Canais', value: po.canais || 0, itemStyle: { color: '#f672a7' } },
        ].filter((d) => d.value > 0);
        chartOrig.setOption({
          tooltip: { trigger: 'item', ...this.tooltipBase(t), formatter: (p) => `${p.name}: <strong>${fmt(p.value)}</strong> (${p.percent}%)` },
          legend: { bottom: 0, textStyle: { color: t.muted }, itemGap: 14 },
          series: [{
            type: 'pie', radius: ['52%', '76%'], center: ['50%', '44%'], avoidLabelOverlap: false,
            itemStyle: { borderColor: t.tipBg, borderWidth: 2 },
            label: { show: true, position: 'center', formatter: () => `{v|${fmt(e.totalGeral)}}\n{l|no total}`, rich: { v: { fontSize: 22, fontWeight: 'bold', color: t.text, lineHeight: 30 }, l: { fontSize: 12, color: t.muted } } },
            labelLine: { show: false }, data,
          }],
        });
      }

      // 2) Série de publicações no período.
      const chartSerie = this.initChart('cEnqSerie');
      if (chartSerie) {
        const serie = e.serie || [];
        const labels = serie.map((p) => `${p.dia.slice(8, 10)}/${p.dia.slice(5, 7)}`);
        chartSerie.setOption({
          tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' }, ...this.tooltipBase(t), formatter: (p) => `${p[0].name}<br/><strong>${fmt(p[0].value)}</strong> publicada(s)` },
          grid: { left: 6, right: 16, top: 12, bottom: 6, containLabel: true },
          xAxis: { type: 'category', data: labels, axisLabel: { color: t.muted, hideOverlap: true }, axisLine: { lineStyle: { color: t.axis } }, axisTick: { show: false } },
          yAxis: { type: 'value', minInterval: 1, axisLabel: { color: t.muted, formatter: (v) => fmtEixo(v) }, splitLine: { lineStyle: { color: t.split } } },
          series: [{
            type: 'bar', barWidth: '58%',
            itemStyle: { color: '#405189', borderRadius: [5, 5, 0, 0] },
            data: serie.map((p) => p.total),
          }],
        });
      }

      // 3) Mais votadas (enquetes + testes com pelo menos 1 voto).
      const chartTop = this.initChart('cEnqTop');
      if (chartTop) {
        const arr = [...(e.top || [])].reverse();
        chartTop.setOption({
          tooltip: {
            trigger: 'axis',
            axisPointer: { type: 'shadow' },
            ...this.tooltipBase(t),
            formatter: (p) => {
              const item = arr[p[0].dataIndex] || {};
              const rotulo = item.quiz ? 'teste' : 'enquete';
              return `${p[0].name}<br/><strong>${fmt(p[0].value)}</strong> votantes<br/><small>${rotulo} · ${item.origem === 'canal' ? 'canal' : 'grupo'} · ${fmt(item.opcoes)} opções</small>`;
            },
          },
          grid: { left: 6, right: 48, top: 6, bottom: 6, containLabel: true },
          xAxis: { type: 'value', minInterval: 1, axisLabel: { color: t.muted, formatter: (v) => fmtEixo(v) }, splitLine: { lineStyle: { color: t.split } } },
          yAxis: { type: 'category', data: arr.map((x) => x.pergunta), axisLabel: { color: t.text, fontSize: 12, width: 220, overflow: 'truncate' }, axisLine: { lineStyle: { color: t.axis } }, axisTick: { show: false } },
          series: [{
            type: 'bar', barWidth: '58%',
            data: arr.map((x) => ({ value: x.votantes, itemStyle: { color: x.quiz ? '#6559cc' : '#0ab39c', borderRadius: [0, 5, 5, 0] } })),
            label: { show: true, position: 'right', color: t.text, fontWeight: 600, formatter: (p) => fmt(p.value) },
          }],
        });
      }
    },

    /**
     * Gráficos de grupos e comunidades. Todos saem da lista de participantes
     * (`analiseGrupos`); sem ela a seção nem aparece.
     */
    renderGrupos() {
      if (!this.temAnaliseGrupos) return;
      const g = this.gruposAnalise;
      const t = this.tema();

      // 1) De onde vêm as participações — evidencia a repetição estrutural.
      const comp = g.composicao || {};
      const chartComp = this.initChart('cGrupComp');
      if (chartComp) {
        const dados = [
          { name: 'Grupos comuns', value: comp.comuns || 0, itemStyle: { color: '#0ab39c' } },
          { name: 'Grupo de avisos', value: comp.avisos || 0, itemStyle: { color: '#f7b84b' } },
          { name: 'Nó de comunidade', value: comp.noComunidade || 0, itemStyle: { color: '#f672a7' } },
        ].filter((d) => d.value > 0);
        chartComp.setOption({
          tooltip: { trigger: 'item', ...this.tooltipBase(t), formatter: (p) => `${p.name}<br/><strong>${fmt(p.value)}</strong> participações (${p.percent}%)` },
          legend: { bottom: 0, textStyle: { color: t.text, fontSize: 11 } },
          series: [{
            type: 'pie', radius: ['48%', '72%'], center: ['50%', '44%'], avoidLabelOverlap: true,
            itemStyle: { borderColor: t.card, borderWidth: 2 },
            label: { show: false }, data: dados,
          }],
        });
      }

      // 2) Em quantos grupos cada pessoa está.
      const chartDist = this.initChart('cGrupDist');
      if (chartDist) {
        const dist = g.distribuicao || [];
        chartDist.setOption({
          tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' }, ...this.tooltipBase(t), formatter: (p) => `${p[0].name}<br/><strong>${fmt(p[0].value)}</strong> pessoas` },
          grid: { left: 6, right: 16, top: 16, bottom: 6, containLabel: true },
          xAxis: {
            type: 'category',
            data: dist.map((d) => (d.ehCauda ? `${d.grupos}+ grupos` : `${d.grupos} grupo${d.grupos > 1 ? 's' : ''}`)),
            axisLabel: { color: t.muted, fontSize: 11 }, axisLine: { lineStyle: { color: t.axis } }, axisTick: { show: false },
          },
          yAxis: { type: 'value', axisLabel: { color: t.muted, formatter: (v) => fmtEixo(v) }, splitLine: { lineStyle: { color: t.split } } },
          series: [{
            type: 'bar', barWidth: '52%',
            data: dist.map((d, i) => ({ value: d.pessoas, itemStyle: { color: PALETTE[i % PALETTE.length], borderRadius: [5, 5, 0, 0] } })),
            label: { show: true, position: 'top', color: t.text, fontWeight: 600, formatter: (p) => fmt(p.value) },
          }],
        });
      }

      // 3) Pessoas distintas por comunidade (nó + avisos + subgrupos juntos).
      const comunidades = g.comunidades || [];
      if (comunidades.length) {
        const chartCom = this.initChart('cGrupCom');
        if (chartCom) {
          const arr = [...comunidades].reverse();
          chartCom.setOption({
            tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' }, ...this.tooltipBase(t), formatter: (p) => `${p[0].name}<br/><strong>${fmt(p[0].value)}</strong> pessoas distintas` },
            grid: { left: 6, right: 48, top: 6, bottom: 6, containLabel: true },
            xAxis: { type: 'value', axisLabel: { color: t.muted, formatter: (v) => fmtEixo(v) }, splitLine: { lineStyle: { color: t.split } } },
            yAxis: { type: 'category', data: arr.map((c) => c.nome), axisLabel: { color: t.text, fontSize: 12, width: 130, overflow: 'truncate' }, axisLine: { lineStyle: { color: t.axis } }, axisTick: { show: false } },
            series: [{
              type: 'bar', barWidth: '58%',
              data: arr.map((c, i) => ({ value: c.pessoas, itemStyle: { color: PALETTE[i % PALETTE.length], borderRadius: [0, 5, 5, 0] } })),
              label: { show: true, position: 'right', color: t.text, fontWeight: 600, formatter: (p) => fmt(p.value) },
            }],
          });
        }
      }

      // 4) Participantes que já são contato da base.
      const chartBase = this.initChart('cGrupBase');
      if (chartBase) {
        const b = g.base || {};
        chartBase.setOption({
          tooltip: { trigger: 'item', ...this.tooltipBase(t), formatter: (p) => `${p.name}<br/><strong>${fmt(p.value)}</strong> pessoas (${p.percent}%)` },
          legend: { bottom: 0, textStyle: { color: t.text, fontSize: 11 } },
          series: [{
            type: 'pie', radius: ['48%', '72%'], center: ['50%', '44%'],
            itemStyle: { borderColor: t.card, borderWidth: 2 }, label: { show: false },
            data: [
              { name: 'Já cadastrados', value: b.naBase || 0, itemStyle: { color: '#405189' } },
              { name: 'Fora da base', value: b.foraDaBase || 0, itemStyle: { color: '#f06548' } },
            ].filter((d) => d.value > 0),
          }],
        });
      }

      // 5) Maiores grupos.
      const chartTop = this.initChart('cGrupTop');
      if (chartTop) {
        const arr = [...(g.topGrupos || [])].reverse();
        const corPorTipo = { comunidade: '#f672a7', avisos: '#f7b84b', comum: '#0ab39c' };
        chartTop.setOption({
          tooltip: {
            trigger: 'axis',
            axisPointer: { type: 'shadow' },
            ...this.tooltipBase(t),
            formatter: (p) => {
              const item = arr[p[0].dataIndex] || {};
              const rotulo = { comunidade: 'nó de comunidade', avisos: 'grupo de avisos', comum: 'grupo' }[item.tipo] || 'grupo';
              return `${p[0].name}<br/><strong>${fmt(p[0].value)}</strong> participantes<br/><small>${rotulo}</small>`;
            },
          },
          grid: { left: 6, right: 48, top: 6, bottom: 6, containLabel: true },
          xAxis: { type: 'value', axisLabel: { color: t.muted, formatter: (v) => fmtEixo(v) }, splitLine: { lineStyle: { color: t.split } } },
          yAxis: { type: 'category', data: arr.map((x) => x.nome), axisLabel: { color: t.text, fontSize: 12, width: 200, overflow: 'truncate' }, axisLine: { lineStyle: { color: t.axis } }, axisTick: { show: false } },
          series: [{
            type: 'bar', barWidth: '58%',
            data: arr.map((x) => ({ value: x.pessoas, itemStyle: { color: corPorTipo[x.tipo] || '#0ab39c', borderRadius: [0, 5, 5, 0] } })),
            label: { show: true, position: 'right', color: t.text, fontWeight: 600, formatter: (p) => fmt(p.value) },
          }],
        });
      }
    },

    renderFunil() {
      if (!this.temFunil) return;
      const chart = this.initChart('cFunil'); if (!chart) return;
      const t = this.tema();
      const funil = [...(this.conversas.funil || [])].reverse();
      // Uma cor por TIPO de situação do estágio (não por estágio individual).
      const TIPOS = [
        { key: 'inicial', label: 'Entrada', color: '#f7b84b' },
        { key: 'ativo', label: 'Em andamento', color: '#299cdb' },
        { key: 'finalizado', label: 'Finalizado', color: '#0ab39c' },
      ];
      chart.setOption({
        tooltip: {
          trigger: 'axis',
          axisPointer: { type: 'shadow' },
          ...this.tooltipBase(t),
          formatter: (params) => {
            const tot = params.reduce((s, p) => s + (p.value || 0), 0);
            const atual = params.find((p) => p.value > 0);
            return `${params[0].name}<br/><strong>${fmt(tot)}</strong> conversas${atual ? ` · <span style="color:${atual.color}">${atual.seriesName}</span>` : ''}`;
          },
        },
        legend: { bottom: 0, textStyle: { color: t.muted }, itemGap: 16, data: TIPOS.map((tp) => tp.label) },
        grid: { left: 6, right: 40, top: 6, bottom: 34, containLabel: true },
        xAxis: { type: 'value', axisLabel: { color: t.muted, formatter: (v) => fmtEixo(v) }, axisLine: { lineStyle: { color: t.axis } }, splitLine: { lineStyle: { color: t.split } } },
        yAxis: { type: 'category', data: funil.map((f) => f.nome), axisLabel: { color: t.text, fontSize: 12 }, axisLine: { lineStyle: { color: t.axis } }, axisTick: { show: false } },
        // 3 séries empilhadas (uma por tipo). Cada estágio só tem valor na sua
        // série → a barra sai na cor do seu tipo, e a legenda mapeia as cores.
        series: TIPOS.map((tp) => ({
          name: tp.label,
          type: 'bar',
          stack: 'funil',
          barWidth: '58%',
          itemStyle: { color: tp.color, borderRadius: [0, 5, 5, 0] },
          data: funil.map((f) => (f.tipo === tp.key ? f.total : 0)),
          label: { show: true, position: 'right', color: t.text, fontWeight: 600, formatter: (p) => (p.value ? fmt(p.value) : '') },
        })),
      });
    },

    renderStatus() {
      if (!this.temStatus) return;
      const chart = this.initChart('cStatus'); if (!chart) return;
      const t = this.tema();
      const s = this.contatos.porStatus || {};
      // Uma cor por situação, sempre as três na legenda (mesmo zeradas).
      const data = [
        { value: s.ativo || 0, name: 'Ativos', itemStyle: { color: '#0ab39c' } },
        { value: s.inativo || 0, name: 'Inativos', itemStyle: { color: '#878a99' } },
        { value: s.bloqueado || 0, name: 'Bloqueados', itemStyle: { color: '#f06548' } },
      ];
      chart.setOption({
        tooltip: { trigger: 'item', ...this.tooltipBase(t), formatter: (p) => `${p.name}: <strong>${fmt(p.value)}</strong> (${p.percent}%)` },
        legend: { bottom: 0, textStyle: { color: t.muted }, itemGap: 14 },
        series: [{
          type: 'pie', radius: ['52%', '76%'], center: ['50%', '44%'], avoidLabelOverlap: false,
          itemStyle: { borderColor: t.tipBg, borderWidth: 2 },
          label: { show: true, position: 'center', formatter: () => `{v|${fmt(this.contatos.total)}}\n{l|Total}`, rich: { v: { fontSize: 22, fontWeight: 'bold', color: t.text, lineHeight: 30 }, l: { fontSize: 12, color: t.muted } } },
          labelLine: { show: false }, data,
        }],
      });
    },

    renderVolume() {
      if (!this.temSerie) return;
      const chart = this.initChart('cVolume'); if (!chart) return;
      const t = this.tema();
      const serie = this.mensagens.serie || [];
      const labels = serie.map((p) => `${p.dia.slice(8, 10)}/${p.dia.slice(5, 7)}`);
      const area = (c1, c2) => new echarts.graphic.LinearGradient(0, 0, 0, 1, [{ offset: 0, color: c1 }, { offset: 1, color: c2 }]);
      chart.setOption({
        tooltip: { trigger: 'axis', ...this.tooltipBase(t) },
        legend: { bottom: 0, textStyle: { color: t.muted }, data: ['Recebidas', 'Enviadas'] },
        grid: { left: 6, right: 16, top: 10, bottom: 34, containLabel: true },
        xAxis: { type: 'category', boundaryGap: false, data: labels, axisLabel: { color: t.muted, hideOverlap: true }, axisLine: { lineStyle: { color: t.axis } } },
        yAxis: { type: 'value', axisLabel: { color: t.muted, formatter: (v) => fmtEixo(v) }, splitLine: { lineStyle: { color: t.split } } },
        series: [
          { name: 'Recebidas', type: 'line', smooth: true, symbol: 'none', lineStyle: { width: 2, color: '#0ab39c' }, itemStyle: { color: '#0ab39c' }, areaStyle: { color: area('rgba(10,179,156,0.35)', 'rgba(10,179,156,0.02)') }, data: serie.map((p) => p.entrada) },
          { name: 'Enviadas', type: 'line', smooth: true, symbol: 'none', lineStyle: { width: 2, color: '#405189' }, itemStyle: { color: '#405189' }, areaStyle: { color: area('rgba(64,81,137,0.30)', 'rgba(64,81,137,0.02)') }, data: serie.map((p) => p.saida) },
        ],
      });
    },

    renderFunilCamp() {
      if (!this.temFunilCamp) return;
      const chart = this.initChart('cFunilCamp'); if (!chart) return;
      const t = this.tema();
      const f = this.campanhas.funil || {};
      const data = [
        { value: f.destinatarios || 0, name: 'Destinatários', itemStyle: { color: '#405189' } },
        { value: f.enviados || 0, name: 'Enviados', itemStyle: { color: '#3577f1' } },
        { value: f.entregues || 0, name: 'Entregues', itemStyle: { color: '#0ab39c' } },
        { value: f.lidos || 0, name: 'Lidos', itemStyle: { color: '#51d28c' } },
      ];
      chart.setOption({
        tooltip: { trigger: 'item', ...this.tooltipBase(t), formatter: (p) => `${p.name}: <strong>${fmt(p.value)}</strong>` },
        legend: { bottom: 0, textStyle: { color: t.muted }, itemGap: 10, type: 'scroll' },
        series: [{
          type: 'funnel', top: 10, bottom: 40, left: '8%', right: '8%', minSize: '20%', maxSize: '100%',
          sort: 'descending', gap: 3, funnelAlign: 'center',
          label: { show: true, position: 'inside', color: '#fff', fontWeight: 600, formatter: (p) => fmt(p.value) },
          labelLine: { show: false }, data,
        }],
      });
    },

    renderCrescimento() {
      if (!this.temCrescimento) return;
      const chart = this.initChart('cCresc'); if (!chart) return;
      const t = this.tema();
      const rs = getComputedStyle(document.documentElement);
      const corMarca = rs.getPropertyValue('--brand-primary').trim() || '#971219';
      const rgbMarca = rs.getPropertyValue('--vz-primary-rgb').trim() || '151,18,25';
      const arr = this.contatos.crescimento || [];
      const labels = arr.map((m) => { const [y, mm] = m.mes.split('-'); return `${MESES[Number(mm) - 1]}/${y.slice(2)}`; });
      chart.setOption({
        tooltip: { trigger: 'axis', ...this.tooltipBase(t), formatter: (p) => `${p[0].name}<br/><strong>${fmt(p[0].value)}</strong> novos contatos` },
        grid: { left: 6, right: 16, top: 12, bottom: 6, containLabel: true },
        xAxis: { type: 'category', boundaryGap: false, data: labels, axisLabel: { color: t.muted }, axisLine: { lineStyle: { color: t.axis } } },
        yAxis: { type: 'value', axisLabel: { color: t.muted, formatter: (v) => fmtEixo(v) }, splitLine: { lineStyle: { color: t.split } } },
        series: [{
          type: 'line', smooth: true, symbol: 'circle', symbolSize: 6, lineStyle: { width: 2.5, color: corMarca }, itemStyle: { color: corMarca },
          areaStyle: { color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [{ offset: 0, color: `rgba(${rgbMarca},0.28)` }, { offset: 1, color: `rgba(${rgbMarca},0.02)` }]) },
          data: arr.map((m) => m.total),
        }],
      });
    },

    renderCarga() {
      if (!this.temCarga) return;
      const chart = this.initChart('cCarga'); if (!chart) return;
      const t = this.tema();
      const arr = [...(this.conversas.carga || [])].reverse();
      chart.setOption({
        tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' }, ...this.tooltipBase(t), formatter: (p) => `${p[0].name}<br/><strong>${fmt(p[0].value)}</strong> abertas` },
        grid: { left: 6, right: 40, top: 6, bottom: 6, containLabel: true },
        xAxis: { type: 'value', axisLabel: { color: t.muted, formatter: (v) => fmtEixo(v) }, splitLine: { lineStyle: { color: t.split } } },
        yAxis: { type: 'category', data: arr.map((a) => a.atendente), axisLabel: { color: t.text, fontSize: 12, width: 110, overflow: 'truncate' }, axisLine: { lineStyle: { color: t.axis } }, axisTick: { show: false } },
        series: [{
          type: 'bar', barWidth: '55%',
          data: arr.map((a) => ({ value: a.abertas, itemStyle: { color: a.atendente === 'Não atribuída' ? '#878a99' : '#405189', borderRadius: [0, 5, 5, 0] } })),
          label: { show: true, position: 'right', color: t.text, fontWeight: 600, formatter: (p) => fmt(p.value) },
        }],
      });
    },

    renderCanais() {
      if (!this.temCanais) return;
      const chart = this.initChart('cCanais'); if (!chart) return;
      const t = this.tema();
      const of = this.canais.oficialPorStatus || {};
      const ba = this.canais.baileysPorStatus || {};
      const oficial = { conectados: of.ativo || 0, problemas: of.bloqueado || 0, outros: (of.inativo || 0) + (of.pendente || 0) };
      const baileys = { conectados: ba.conectado || 0, problemas: (ba.banido || 0) + (ba.erro || 0), outros: (ba.desconectado || 0) + (ba.pareando || 0) + (ba.reconectando || 0) };
      const cats = ['WhatsApp Oficial', 'API-B (QR)'];
      const serie = (nome, cor, key) => ({ name: nome, type: 'bar', stack: 'c', barWidth: 40, itemStyle: { color: cor }, emphasis: { focus: 'series' }, data: [oficial[key], baileys[key]] });
      chart.setOption({
        tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' }, ...this.tooltipBase(t) },
        legend: { bottom: 0, textStyle: { color: t.muted }, itemGap: 14 },
        grid: { left: 6, right: 16, top: 10, bottom: 34, containLabel: true },
        xAxis: { type: 'value', minInterval: 1, axisLabel: { color: t.muted, formatter: (v) => fmtEixo(v) }, splitLine: { lineStyle: { color: t.split } } },
        yAxis: { type: 'category', data: cats, axisLabel: { color: t.text }, axisLine: { lineStyle: { color: t.axis } }, axisTick: { show: false } },
        series: [serie('Conectados', '#0ab39c', 'conectados'), serie('Com problema', '#f06548', 'problemas'), serie('Outros', '#878a99', 'outros')],
      });
    },

    renderEtiquetas() {
      if (!this.temEtiquetas) return;
      const chart = this.initChart('cEtiq'); if (!chart) return;
      const t = this.tema();
      const arr = [...(this.d.topEtiquetas || [])].reverse();
      chart.setOption({
        tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' }, ...this.tooltipBase(t), formatter: (p) => `${p[0].name}<br/><strong>${fmt(p[0].value)}</strong> contatos` },
        grid: { left: 6, right: 40, top: 6, bottom: 6, containLabel: true },
        xAxis: { type: 'value', axisLabel: { color: t.muted, formatter: (v) => fmtEixo(v) }, splitLine: { lineStyle: { color: t.split } } },
        yAxis: { type: 'category', data: arr.map((r) => r.rotulo), axisLabel: { color: t.text, fontSize: 12, width: 120, overflow: 'truncate' }, axisLine: { lineStyle: { color: t.axis } }, axisTick: { show: false } },
        series: [{
          type: 'bar', barWidth: '58%',
          data: arr.map((r, i) => ({ value: r.total, itemStyle: { color: r.cor || PALETTE[i % PALETTE.length], borderRadius: [0, 5, 5, 0] } })),
          label: { show: true, position: 'right', color: t.text, fontWeight: 600, formatter: (p) => fmt(p.value) },
        }],
      });
    },

    renderEngajamento() {
      if (!this.temEngajamento) return;
      const chart = this.initChart('cEng'); if (!chart) return;
      const t = this.tema();
      const arr = this.engUsuarios.filter((u) => u.total > 0).slice(0, 10).reverse();
      chart.setOption({
        tooltip: {
          trigger: 'axis', axisPointer: { type: 'shadow' }, ...this.tooltipBase(t),
          formatter: (p) => {
            const u = arr[p[0].dataIndex] || {};
            return `${u.nome}<br/><strong>${fmt(u.total)}</strong> ações<br/>`
              + `<small>${fmt(u.mensagensConversa)} conversa · ${fmt(u.mensagensGrupo)} grupo · ${fmt(u.mensagensCanal)} canal<br/>`
              + `${fmt(u.campanhas)} campanha · ${fmt(u.contatosCriados)} contato · ${fmt(u.notas)} nota</small>`;
          },
        },
        grid: { left: 6, right: 44, top: 6, bottom: 6, containLabel: true },
        xAxis: { type: 'value', axisLabel: { color: t.muted, formatter: (v) => fmtEixo(v) }, splitLine: { lineStyle: { color: t.split } } },
        yAxis: { type: 'category', data: arr.map((u) => u.nome), axisLabel: { color: t.text, fontSize: 12, width: 120, overflow: 'truncate' }, axisLine: { lineStyle: { color: t.axis } }, axisTick: { show: false } },
        series: [{
          type: 'bar', barWidth: '60%',
          data: arr.map((u) => u.total),
          itemStyle: { color: new echarts.graphic.LinearGradient(0, 0, 1, 0, [{ offset: 0, color: '#405189' }, { offset: 1, color: '#6a7cc4' }]), borderRadius: [0, 5, 5, 0] },
          label: { show: true, position: 'right', color: t.text, fontWeight: 600, formatter: (p) => fmt(p.value) },
        }],
      });
    },

    abrirModalEng() {
      if (this.engajamento) this.modalEng = true;
    },
  },
};
</script>

<template>
  <Layout>
    <div class="dash-wrap">
      <!-- ============ Cabeçalho ============ -->
      <header class="dash-head">
        <div class="dash-head-title">
          <span class="dash-head-badge"><i class="ri-dashboard-2-line"></i></span>
          <div class="dash-head-text">
            <h4 class="mb-0">Painel de gestão</h4>
            <small class="text-muted">
              Visão geral da operação
              <span v-if="atualizadoEm"> · atualizado às {{ atualizadoEm }}</span>
            </small>
          </div>
        </div>
        <div class="dash-head-actions">
          <div class="dash-period" role="group" aria-label="Período">
            <button
              v-for="p in periodos"
              :key="p"
              type="button"
              class="dash-period-btn"
              :class="{ active: periodo === p }"
              :disabled="loading"
              @click="trocarPeriodo(p)"
            >{{ p }}d</button>
          </div>
          <button type="button" class="btn btn-sm btn-soft-primary dash-refresh" :disabled="loading" @click="carregar(true)" title="Atualizar">
            <i class="ri-refresh-line" :class="{ spin: loading }"></i>
          </button>
        </div>
      </header>

      <div v-if="erro" class="alert alert-danger d-flex align-items-center gap-2">
        <i class="ri-error-warning-line fs-5"></i>
        <span class="flex-grow-1">{{ erro }}</span>
        <button type="button" class="btn btn-sm btn-danger" @click="carregar(true)">Tentar de novo</button>
      </div>

      <!-- ============ Skeleton (primeira carga) ============ -->
      <template v-if="loading && !dados">
        <BRow class="g-3 kpi-row">
          <BCol v-for="n in 6" :key="'sk' + n" cols="6" lg="4" xxl="2">
            <div class="kpi-card skeleton-card">
              <div class="sk-line sk-40"></div>
              <div class="sk-line sk-70 sk-lg"></div>
              <div class="sk-line sk-90"></div>
            </div>
          </BCol>
        </BRow>
        <BRow class="g-3 dash-grid">
          <BCol cols="12" lg="8"><div class="dash-card skeleton-chart"></div></BCol>
          <BCol cols="12" lg="4"><div class="dash-card skeleton-chart"></div></BCol>
        </BRow>
      </template>

      <template v-else-if="dados">
        <!-- ============ KPI cards ============ -->
        <BRow class="g-3 kpi-row">
          <BCol v-for="k in kpis" :key="k.key" cols="6" lg="4" xxl="2">
            <div class="kpi-card" :style="{ '--accent': k.hex }">
              <div class="kpi-top">
                <span class="kpi-icon" :style="{ background: k.hex + '22', color: k.hex }"><i :class="k.icon"></i></span>
                <span
                  v-if="k.delta !== null && k.delta !== undefined"
                  class="kpi-delta"
                  :class="k.delta >= 0 ? 'up' : 'down'"
                >
                  <i :class="k.delta >= 0 ? 'ri-arrow-up-line' : 'ri-arrow-down-line'"></i>{{ Math.abs(k.delta) }}%
                </span>
              </div>
              <div class="kpi-value">{{ fmt(animado[k.key]) }}<span v-if="k.suffix" class="kpi-suffix">{{ k.suffix }}</span></div>
              <div class="kpi-label">{{ k.label }}</div>
              <div class="kpi-sub text-muted">{{ k.sub }}</div>
            </div>
          </BCol>
        </BRow>

        <!-- ============ Linha 1: Funil de atendimento + Status ============ -->
        <BRow class="g-3 dash-grid">
          <BCol cols="12" lg="8">
            <div class="dash-card">
              <div class="dash-card-head">
                <h6><i class="ri-filter-3-line me-1"></i>Funil de atendimento</h6>
                <span class="dash-card-badge">{{ fmt(conversas.abertas) }} abertas</span>
              </div>
              <div v-if="temFunil" ref="cFunil" class="dash-chart dash-chart-tall"></div>
              <div v-else class="dash-empty"><i class="ri-inbox-line"></i><span>Nenhuma conversa nos estágios ainda.</span></div>
            </div>
          </BCol>
          <BCol cols="12" lg="4">
            <div class="dash-card">
              <div class="dash-card-head"><h6><i class="ri-pie-chart-2-line me-1"></i>Contatos por status</h6></div>
              <div v-if="temStatus" ref="cStatus" class="dash-chart dash-chart-tall"></div>
              <div v-else class="dash-empty"><i class="ri-contacts-book-line"></i><span>Sem contatos cadastrados.</span></div>
            </div>
          </BCol>
        </BRow>

        <!-- ============ Linha 2: Volume mensagens + Funil campanhas ============ -->
        <BRow class="g-3 dash-grid">
          <BCol cols="12" lg="8">
            <div class="dash-card">
              <div class="dash-card-head">
                <h6><i class="ri-exchange-line me-1"></i>Volume de mensagens</h6>
                <span class="dash-card-badge">{{ periodo }} dias</span>
              </div>
              <div v-if="temSerie" ref="cVolume" class="dash-chart"></div>
              <div v-else class="dash-empty"><i class="ri-chat-off-line"></i><span>Sem mensagens no período.</span></div>
            </div>
          </BCol>
          <BCol cols="12" lg="4">
            <div class="dash-card">
              <div class="dash-card-head">
                <h6><i class="ri-send-plane-2-line me-1"></i>Funil de entrega</h6>
                <span v-if="temFunilCamp" class="dash-card-badge">{{ campanhas.taxaEntrega }}% entrega</span>
              </div>
              <div v-if="temFunilCamp" ref="cFunilCamp" class="dash-chart"></div>
              <div v-else class="dash-empty"><i class="ri-megaphone-line"></i><span>Nenhuma campanha disparada.</span></div>
            </div>
          </BCol>
        </BRow>

        <!-- ============ Linha 3: Crescimento + Carga atendentes ============ -->
        <BRow class="g-3 dash-grid">
          <BCol cols="12" lg="6">
            <div class="dash-card">
              <div class="dash-card-head"><h6><i class="ri-line-chart-line me-1"></i>Crescimento da base (12 meses)</h6></div>
              <div v-if="temCrescimento" ref="cCresc" class="dash-chart"></div>
              <div v-else class="dash-empty"><i class="ri-seedling-line"></i><span>Sem histórico suficiente.</span></div>
            </div>
          </BCol>
          <BCol cols="12" lg="6">
            <div class="dash-card">
              <div class="dash-card-head"><h6><i class="ri-user-star-line me-1"></i>Carga por atendente</h6></div>
              <div v-if="temCarga" ref="cCarga" class="dash-chart"></div>
              <div v-else class="dash-empty"><i class="ri-user-line"></i><span>Nenhuma conversa aberta atribuída.</span></div>
            </div>
          </BCol>
        </BRow>

        <!-- ============ Linha 4: Saúde canais + Top etiquetas ============ -->
        <BRow class="g-3 dash-grid">
          <BCol cols="12" lg="6">
            <div class="dash-card">
              <div class="dash-card-head">
                <h6><i class="ri-base-station-line me-1"></i>Saúde dos canais</h6>
                <span class="dash-card-badge">{{ fmt(canaisConectados) }}/{{ fmt(canaisTotal) }} on</span>
              </div>
              <div v-if="temCanais" ref="cCanais" class="dash-chart dash-chart-short"></div>
              <div v-else class="dash-empty"><i class="ri-plug-line"></i><span>Nenhum canal cadastrado.</span></div>
            </div>
          </BCol>
          <BCol cols="12" lg="6">
            <div class="dash-card">
              <div class="dash-card-head"><h6><i class="ri-price-tag-3-line me-1"></i>Top etiquetas</h6></div>
              <div v-if="temEtiquetas" ref="cEtiq" class="dash-chart dash-chart-short"></div>
              <div v-else class="dash-empty"><i class="ri-price-tag-line"></i><span>Nenhuma etiqueta aplicada.</span></div>
            </div>
          </BCol>
        </BRow>

        <!-- ============ Grupos e comunidades ============ -->
        <BRow v-if="temAnaliseGrupos" class="g-3 dash-grid">
          <BCol cols="12">
            <div class="dash-card">
              <div class="dash-card-head">
                <h6><i class="ri-group-2-line me-1"></i>Grupos e comunidades</h6>
                <span class="dash-card-badge">
                  {{ fmt(gruposAnalise.pessoasUnicas) }} pessoas · {{ fmt(gruposAnalise.participacoes) }} participações
                </span>
              </div>
              <div class="grupos-chips">
                <div v-for="ch in gruposChips" :key="ch.label" class="eng-chip">
                  <span class="eng-chip-ic" :style="{ background: ch.hex }"><i :class="ch.icon"></i></span>
                  <div><strong>{{ fmt(ch.value) }}</strong><small>{{ ch.label }}</small></div>
                </div>
              </div>
            </div>
          </BCol>

          <BCol cols="12" lg="6">
            <div class="dash-card">
              <div class="dash-card-head">
                <h6><i class="ri-pie-chart-2-line me-1"></i>De onde vêm as participações</h6>
              </div>
              <div ref="cGrupComp" class="dash-chart dash-chart-short"></div>
              <small class="dash-nota">
                Nó de comunidade e grupo de avisos repetem as mesmas pessoas dos subgrupos.
              </small>
            </div>
          </BCol>

          <BCol cols="12" lg="6">
            <div class="dash-card">
              <div class="dash-card-head"><h6><i class="ri-bar-chart-grouped-line me-1"></i>Em quantos grupos cada pessoa está</h6></div>
              <div ref="cGrupDist" class="dash-chart dash-chart-short"></div>
            </div>
          </BCol>

          <BCol cols="12" lg="6">
            <div class="dash-card">
              <div class="dash-card-head"><h6><i class="ri-community-line me-1"></i>Pessoas por comunidade</h6></div>
              <div v-if="gruposAnalise.comunidades && gruposAnalise.comunidades.length" ref="cGrupCom" class="dash-chart dash-chart-short"></div>
              <div v-else class="dash-empty"><i class="ri-community-line"></i><span>Nenhuma comunidade sincronizada.</span></div>
            </div>
          </BCol>

          <BCol cols="12" lg="6">
            <div class="dash-card">
              <div class="dash-card-head"><h6><i class="ri-contacts-book-2-line me-1"></i>Participantes já cadastrados</h6></div>
              <div ref="cGrupBase" class="dash-chart dash-chart-short"></div>
              <small class="dash-nota">
                Quem está nos grupos e ainda não é contato da base é oportunidade de cadastro.
              </small>
            </div>
          </BCol>

          <BCol cols="12">
            <div class="dash-card">
              <div class="dash-card-head"><h6><i class="ri-trophy-line me-1"></i>Maiores grupos</h6></div>
              <div ref="cGrupTop" class="dash-chart"></div>
            </div>
          </BCol>
        </BRow>

        <!-- ============ Enquetes e testes ============ -->
        <BRow v-if="temEnquetes" class="g-3 dash-grid">
          <BCol cols="12">
            <div class="dash-card">
              <div class="dash-card-head">
                <h6><i class="ri-bar-chart-box-line me-1"></i>Enquetes e testes</h6>
                <span class="dash-card-badge">
                  {{ fmt(enq.totalGeral) }} publicadas · {{ fmt(enq.votosTotais) }} votos
                </span>
              </div>
              <div class="grupos-chips">
                <div v-for="ch in enqChips" :key="ch.label" class="eng-chip">
                  <span class="eng-chip-ic" :style="{ background: ch.hex }"><i :class="ch.icon"></i></span>
                  <div><strong>{{ fmt(ch.value) }}</strong><small>{{ ch.label }}</small></div>
                </div>
              </div>
            </div>
          </BCol>

          <BCol cols="12" lg="4">
            <div class="dash-card">
              <div class="dash-card-head"><h6><i class="ri-pie-chart-2-line me-1"></i>Onde foram publicadas</h6></div>
              <div ref="cEnqOrig" class="dash-chart dash-chart-short"></div>
            </div>
          </BCol>

          <BCol cols="12" lg="8">
            <div class="dash-card">
              <div class="dash-card-head">
                <h6><i class="ri-calendar-event-line me-1"></i>Publicações no período</h6>
                <span class="dash-card-badge">{{ periodo }} dias</span>
              </div>
              <div v-if="enqTemSerie" ref="cEnqSerie" class="dash-chart dash-chart-short"></div>
              <div v-else class="dash-empty"><i class="ri-calendar-line"></i><span>Nenhuma enquete publicada no período.</span></div>
            </div>
          </BCol>

          <BCol cols="12">
            <div class="dash-card">
              <div class="dash-card-head"><h6><i class="ri-trophy-line me-1"></i>Mais votadas</h6></div>
              <div v-if="enqTemTop" ref="cEnqTop" class="dash-chart"></div>
              <div v-else class="dash-empty"><i class="ri-inbox-line"></i><span>Ainda sem votos apurados.</span></div>
            </div>
          </BCol>
        </BRow>

        <!-- ============ Engajamento da equipe ============ -->
        <BRow class="g-3 dash-grid">
          <BCol cols="12">
            <div class="dash-card">
              <div class="dash-card-head">
                <h6><i class="ri-team-line me-1"></i>Engajamento da equipe</h6>
                <button type="button" class="dash-detail-btn" :disabled="!engajamento" @click="abrirModalEng">
                  <i class="ri-table-line me-1"></i>Ver por usuário
                </button>
              </div>
              <div v-if="engajamento" class="eng-chips">
                <div v-for="ch in engChips" :key="ch.label" class="eng-chip" @click="abrirModalEng">
                  <span class="eng-chip-ic" :style="{ background: ch.hex }"><i :class="ch.icon"></i></span>
                  <div><strong>{{ fmt(ch.value) }}</strong><small>{{ ch.label }}</small></div>
                </div>
              </div>
              <div v-if="temEngajamento" ref="cEng" class="dash-chart eng-chart" @click="abrirModalEng"></div>
              <div v-else class="dash-empty"><i class="ri-bar-chart-2-line"></i><span>Sem atividade de usuários no período.</span></div>
            </div>
          </BCol>
        </BRow>
      </template>

      <EngajamentoUsuariosModal v-if="engajamento" :visivel="modalEng" :dados="engajamento" @fechar="modalEng = false" />
    </div>
  </Layout>
</template>

<style scoped>
.dash-wrap { padding-bottom: 1rem; }

/* ---------- Cabeçalho ---------- */
.dash-head {
  display: flex; align-items: center; justify-content: space-between;
  flex-wrap: wrap; gap: 0.75rem; margin-bottom: 1rem;
}
.dash-head-title { display: flex; align-items: center; gap: 0.75rem; }
.dash-head-badge {
  width: 44px; height: 44px; border-radius: 12px; flex-shrink: 0;
  display: flex; align-items: center; justify-content: center; font-size: 1.4rem; color: #fff;
  background: linear-gradient(135deg, var(--brand-primary, #971219), color-mix(in srgb, var(--brand-primary, #971219) 82%, #fff));
  box-shadow: 0 4px 12px rgba(var(--vz-primary-rgb, 151, 18, 25), 0.35);
}
[data-bs-theme='dark'] .dash-head-badge { background: linear-gradient(135deg, var(--brand-primary-dark, #3f060a), color-mix(in srgb, var(--brand-primary-dark, #3f060a) 72%, #fff)); }
.dash-head h4 { font-weight: 600; letter-spacing: -0.01em; }
.dash-head-actions { display: flex; align-items: center; gap: 0.5rem; }

/* No mobile o topo do app já indica que é o Dashboard: esconde o bloco de título
   inteiro (badge + texto) e transforma o período num segmented control full-width
   (3 segmentos iguais) + refresh alinhado à direita na mesma altura. */
@media (max-width: 767.98px) {
  .dash-head-title { display: none; }
  .dash-head-actions { width: 100%; }
  .dash-period { flex: 1; }
  .dash-period-btn { flex: 1; padding: 7px 4px; }
  .dash-refresh { flex-shrink: 0; align-self: stretch; padding: 0 14px; }
}

.dash-period {
  display: inline-flex; background: var(--vz-secondary-bg, #fff); border-radius: 999px; padding: 3px;
}
[data-bs-theme='dark'] .dash-period { background: rgba(255, 255, 255, 0.06); }
.dash-period-btn {
  border: 0; background: transparent; color: var(--vz-body-color);
  font-size: 0.8rem; font-weight: 600; padding: 5px 14px; border-radius: 999px; cursor: pointer; transition: all 0.15s;
}
.dash-period-btn:hover:not(.active) { background: rgba(0, 0, 0, 0.06); }
[data-bs-theme='dark'] .dash-period-btn:hover:not(.active) { background: rgba(255, 255, 255, 0.08); }
.dash-period-btn.active { background: var(--brand-primary, #971219); color: #fff; box-shadow: 0 2px 6px rgba(var(--vz-primary-rgb, 151, 18, 25), 0.3); }
[data-bs-theme='dark'] .dash-period-btn.active { background: var(--brand-primary, #b5171f); }
.dash-refresh { border-radius: 10px; }

/* ---------- KPI cards ---------- */
.kpi-card {
  position: relative; height: 100%; background: var(--vz-secondary-bg, #fff);
  border: 1px solid var(--vz-border-color, #e9ebec); border-radius: 14px; padding: 1rem 1.1rem;
  overflow: hidden; transition: transform 0.18s ease, box-shadow 0.18s ease;
}
.kpi-card::before {
  content: ''; position: absolute; top: 0; left: 0; right: 0; height: 3px;
  background: var(--accent, #405189); opacity: 0.9;
}
.kpi-card:hover { transform: translateY(-3px); box-shadow: 0 8px 22px rgba(0, 0, 0, 0.08); }
[data-bs-theme='dark'] .kpi-card:hover { box-shadow: 0 8px 22px rgba(0, 0, 0, 0.35); }
.kpi-top { display: flex; align-items: center; justify-content: space-between; margin-bottom: 0.85rem; }
.kpi-icon {
  width: 40px; height: 40px; border-radius: 11px; display: flex; align-items: center; justify-content: center; font-size: 1.25rem;
}
.kpi-delta { font-size: 0.72rem; font-weight: 700; padding: 2px 8px; border-radius: 999px; display: inline-flex; align-items: center; gap: 2px; }
.kpi-delta.up { color: #0ab39c; background: rgba(10, 179, 156, 0.12); }
.kpi-delta.down { color: #f06548; background: rgba(240, 101, 72, 0.12); }
.kpi-value { font-size: 1.7rem; font-weight: 700; line-height: 1.1; color: var(--vz-body-color); letter-spacing: -0.02em; }
.kpi-suffix { font-size: 1rem; font-weight: 600; color: var(--vz-secondary-color, #878a99); }
.kpi-label { font-size: 0.82rem; font-weight: 600; color: var(--vz-body-color); margin-top: 0.35rem; }
.kpi-sub { font-size: 0.72rem; margin-top: 0.15rem; line-height: 1.3; }

/* ---------- Chart cards ---------- */
.dash-card {
  height: 100%; background: var(--vz-secondary-bg, #fff); border: 1px solid var(--vz-border-color, #e9ebec);
  border-radius: 14px; padding: 1rem 1.1rem;
}
.dash-card-head { display: flex; align-items: center; justify-content: space-between; margin-bottom: 0.5rem; gap: 0.5rem; }
.dash-card-head h6 { margin: 0; font-weight: 600; font-size: 0.92rem; color: var(--vz-body-color); }
.dash-card-badge {
  font-size: 0.7rem; font-weight: 600; padding: 3px 9px; border-radius: 999px; white-space: nowrap;
  background: var(--vz-light, #f3f6f9); color: var(--vz-secondary-color, #878a99);
}
[data-bs-theme='dark'] .dash-card-badge { background: rgba(255, 255, 255, 0.06); }
.dash-chart { width: 100%; height: 300px; }
.dash-chart-tall { height: 340px; }
.dash-chart-short { height: 240px; }

.dash-empty {
  height: 240px; display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 0.5rem;
  color: var(--vz-secondary-color, #878a99); text-align: center;
}
.dash-empty i { font-size: 2rem; opacity: 0.5; }
.dash-empty span { font-size: 0.82rem; }

/* ---------- Skeleton ---------- */
.skeleton-card { min-height: 120px; }
.skeleton-chart { height: 340px; }
.sk-line, .skeleton-chart { position: relative; overflow: hidden; background: var(--vz-light, #eef1f4); border-radius: 8px; }
[data-bs-theme='dark'] .sk-line, [data-bs-theme='dark'] .skeleton-chart { background: rgba(255, 255, 255, 0.05); }
.sk-line { height: 12px; margin-bottom: 0.7rem; }
.sk-line.sk-lg { height: 26px; }
.sk-40 { width: 40%; } .sk-70 { width: 70%; } .sk-90 { width: 90%; }
.sk-line::after, .skeleton-chart::after {
  content: ''; position: absolute; inset: 0; transform: translateX(-100%);
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.35), transparent);
  animation: sk-shimmer 1.4s infinite;
}
[data-bs-theme='dark'] .sk-line::after, [data-bs-theme='dark'] .skeleton-chart::after {
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.06), transparent);
}
@keyframes sk-shimmer { 100% { transform: translateX(100%); } }

.spin { display: inline-block; animation: dash-spin 0.9s linear infinite; }
@keyframes dash-spin { 100% { transform: rotate(360deg); } }

/* ---------- Mobile ---------- */
@media (max-width: 575.98px) {
  .kpi-value { font-size: 1.4rem; }
  .kpi-card { padding: 0.85rem 0.9rem; border-radius: 12px; }
  .dash-card { padding: 0.85rem 0.9rem; border-radius: 12px; }
  .dash-chart { height: 260px; }
  .dash-chart-tall { height: 300px; }
  .dash-chart-short { height: 220px; }
  .dash-head h4 { font-size: 1.1rem; }
}

/* ---------- Engajamento ---------- */
.dash-detail-btn {
  border: 1px solid var(--vz-border-color); background: transparent; color: var(--vz-body-color);
  font-size: 0.78rem; font-weight: 600; padding: 4px 12px; border-radius: 8px; cursor: pointer; transition: background 0.15s;
}
.dash-detail-btn:hover:not(:disabled) { background: var(--vz-secondary-bg); }
.dash-detail-btn:disabled { opacity: 0.5; cursor: default; }
.grupos-chips { display: grid; grid-template-columns: repeat(5, 1fr); gap: 0.6rem; }
@media (max-width: 991.98px) { .grupos-chips { grid-template-columns: repeat(3, 1fr); } }
@media (max-width: 575.98px) { .grupos-chips { grid-template-columns: repeat(2, 1fr); } }
.dash-nota { display: block; margin-top: 0.35rem; color: var(--vz-secondary-color, #878a99); font-size: 0.75rem; line-height: 1.3; }
.eng-chips { display: grid; grid-template-columns: repeat(6, 1fr); gap: 0.6rem; margin-bottom: 0.75rem; }
.eng-chip {
  display: flex; align-items: center; gap: 0.5rem; padding: 0.5rem 0.6rem;
  border: 1px solid var(--vz-border-color); border-radius: 10px; cursor: pointer; transition: transform 0.15s;
}
.eng-chip:hover { transform: translateY(-2px); }
.eng-chip-ic { width: 32px; height: 32px; border-radius: 9px; display: flex; align-items: center; justify-content: center; color: #fff; font-size: 1rem; flex-shrink: 0; }
.eng-chip strong { display: block; font-size: 1.05rem; line-height: 1.1; color: var(--vz-body-color); }
.eng-chip small { color: var(--vz-secondary-color); font-size: 0.68rem; }
.eng-chart { cursor: pointer; }
@media (max-width: 991.98px) { .eng-chips { grid-template-columns: repeat(3, 1fr); } }
@media (max-width: 575.98px) { .eng-chips { grid-template-columns: repeat(2, 1fr); } }
</style>
